import { Watcher } from '../utils/Watcher.js';
import { DebugLabelController } from '../web-elements/DebugLabel.js';
/**
 * Handler for selectable instances of a specified `type`.
 * - Accomulates applicable `SelectableDescription`'s.
 * - Watches when instance is created or disposed.
 * - Creates, populates and updates UI `DebugLabel`'s that represent these selectable instances.
 */
export class SelectableHandler {
    constructor(type, _viewer, _debug_layer, _config, descriptions = []) {
        var _a, _b;
        this.type = type;
        this._viewer = _viewer;
        this._debug_layer = _debug_layer;
        this._config = _config;
        this._cache = new Map();
        this._instances = new Map();
        this._is_visible = true;
        this.name = (_a = this._config.name) !== null && _a !== void 0 ? _a : type.name;
        this._is_enabled = (_b = this._config.is_enabled) !== null && _b !== void 0 ? _b : true;
        this._descriptions = descriptions.map(description => this.to_applied_description(description));
        Watcher.subscriptions.add({ subscriber: this, watching: type });
    }
    on_object_created(instance) {
        this._instances.set(instance, this.create_element(instance));
    }
    on_object_disposed(instance) {
        const label = this._instances.get(instance);
        if (label) {
            label.hide();
        }
        this._instances.delete(instance);
        this._instances.forEach(controller => controller.remove_context_menu());
        const selected = this._debug_layer.tools.get_selected();
        if (selected && selected.instance === instance) {
            this._debug_layer.tools.deselect();
        }
    }
    show() {
        this._is_visible = true;
        this.update();
    }
    hide() {
        this._is_visible = false;
        this.update();
    }
    set_enabled(is_enabled) {
        this._is_enabled = is_enabled;
        this.update();
    }
    is_enabled() {
        return this._is_enabled;
    }
    add_description(description) {
        this._descriptions.push(this.to_applied_description(description));
        this._cache.clear();
    }
    get_applied_descriptions() {
        return this._descriptions;
    }
    update() {
        var _a;
        for (const [instance, label] of this._instances) {
            const { world_position } = this.get_selectable(instance);
            const { x, y, z } = world_position(instance);
            const canvas_pos = this._viewer.project_world_position_to_canvas(x, y, z);
            if (canvas_pos && this._is_enabled && this._is_visible) {
                const { x, y } = canvas_pos;
                label.set_position(x, y);
                const tools = this._debug_layer.tools;
                if (((_a = tools.get_selected()) === null || _a === void 0 ? void 0 : _a.instance) === instance) {
                    this._debug_layer.overlay.draw_circle(x, y, 15, 'green', 1);
                    label.hide();
                }
                else {
                    label.show();
                }
            }
            else {
                label.hide();
            }
        }
    }
    dispose() {
        this._instances.forEach(label => label.hide());
        this._instances.clear();
        this._cache.clear();
        this._descriptions.length = 0;
        Watcher.subscriptions.remove(this);
    }
    create_selectable() {
        const { icon, tooltip, world_position } = this._config;
        return {
            icon: icon !== null && icon !== void 0 ? icon : '?',
            tooltip: tooltip !== null && tooltip !== void 0 ? tooltip : 'instance',
            world_position: world_position,
            on_select: [],
            on_deselect: [],
            transform: null,
            initiate_tabs: []
        };
    }
    get_selectable(instance) {
        let selectable = this._cache.get(instance);
        if (selectable) {
            return selectable;
        }
        selectable = this.create_selectable();
        this._descriptions.forEach(applied_description => {
            if (!applied_description.is_enabled())
                return;
            const { type } = applied_description.description;
            if (instance instanceof type) {
                const { transform, on_select, on_deselect, initiate_tabs } = applied_description.description;
                if (transform) {
                    if (!selectable.transform) {
                        selectable.transform = {};
                    }
                    selectable.transform = Object.assign(Object.assign({}, selectable.transform), transform);
                }
                if (on_select) {
                    selectable.on_select.push(on_select);
                }
                if (on_deselect) {
                    selectable.on_deselect.push(on_deselect);
                }
                if (initiate_tabs) {
                    selectable.initiate_tabs.push(...initiate_tabs);
                }
            }
        });
        this._cache.set(instance, selectable);
        return selectable;
    }
    create_element(instance) {
        const { icon, tooltip } = this.get_selectable(instance);
        const label = new DebugLabelController(this._debug_layer.get_container());
        label.set_icon(typeof icon === 'function' ? icon(instance) : icon);
        label.set_title(typeof tooltip === 'function' ? tooltip(instance) : tooltip);
        label.on_click.add(() => {
            var _a, _b, _c;
            this._instances.forEach(controller => controller.remove_context_menu());
            const { transform, world_position, on_select, on_deselect, initiate_tabs } = this.get_selectable(instance);
            this._debug_layer.tools.set_selected({
                instance: instance,
                transform: transform
                    ? {
                        position: (_a = transform.position) === null || _a === void 0 ? void 0 : _a.call(transform, instance),
                        rotation: (_b = transform.rotation) === null || _b === void 0 ? void 0 : _b.call(transform, instance),
                        scale: (_c = transform.scale) === null || _c === void 0 ? void 0 : _c.call(transform, instance),
                        get_world_transform: transform.get_world_transform ? () => transform.get_world_transform(instance) : undefined,
                        get_parent_world_transform: transform.get_parent_world_transform ? () => transform.get_parent_world_transform(instance) : undefined
                    }
                    : null,
                world_position: () => world_position(instance),
                on_select: () => on_select.forEach(f => f(instance)),
                on_deselect: () => on_deselect.forEach(f => f(instance)),
                initiate_tabs: initiate_tabs
            });
        });
        label.on_right_click.add(() => {
            if (label.has_context_menu()) {
                label.remove_context_menu();
            }
            else {
                const content = [];
                this._instances.forEach((controller, instance) => {
                    controller.remove_context_menu();
                    if (label.style.left === controller.style.left && label.style.top === controller.style.top) {
                        const { tooltip } = this.get_selectable(instance);
                        const title = typeof tooltip === 'function' ? tooltip(instance) : tooltip;
                        content.push([title, () => controller.click()]);
                    }
                });
                label.show_context_menu(content);
            }
        });
        return label;
    }
    to_applied_description(description) {
        let { is_enabled } = description;
        return {
            description: description,
            is_enabled: () => is_enabled,
            set_enabled: (value) => {
                is_enabled = value;
                this._cache.clear();
            }
        };
    }
}
//# sourceMappingURL=SelectableHandler.js.map